import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { PosthogService } from '../services/posthog.service';
import { SessionService } from '../services/session.service';
import {
  DENIED_TAB_LABEL_QUERY_PARAM,
  EDIT_ACCESS_DENIED_QUERY_PARAM,
} from '../utils/edit-access-denied-alert';
import {
  canAccessPracticeTab,
  findFirstAccessiblePracticeTab,
  findPracticeTabByRoute,
} from '../utils/practice-tab-access.util';

async function ensureSessionLoaded(
  sessionService: SessionService,
  router: Router
): Promise<boolean> {
  if (!sessionService.isSessionLoaded()) {
    try {
      await firstValueFrom(
        sessionService.checkSessionStatus().pipe(timeout(8000))
      );
    } catch {
      await router.navigate(['/']);
      return false;
    }
  }
  return true;
}

/** Blocks direct navigation to a practice settings tab the user cannot access. */
export function practiceTabGuard(tabRoute: string): CanActivateFn {
  return async () => {
    const router = inject(Router);
    const sessionService = inject(SessionService);
    const posthogService = inject(PosthogService);

    if (!(await ensureSessionLoaded(sessionService, router))) {
      return false;
    }

    const tab = findPracticeTabByRoute(tabRoute);
    if (!tab) {
      return true;
    }

    const featureFlags = {
      referenceReportAnalyzer: posthogService.isReferenceReportAnalyzerEnabled(),
      visualReports: posthogService.isVisualReportsEnabled(),
    };

    if (canAccessPracticeTab(tab, sessionService, featureFlags)) {
      return true;
    }

    const deniedQueryParams = {
      [EDIT_ACCESS_DENIED_QUERY_PARAM]: '1',
      [DENIED_TAB_LABEL_QUERY_PARAM]: tab.label,
    };

    const fallback = findFirstAccessiblePracticeTab(sessionService, featureFlags);
    if (fallback) {
      return router.createUrlTree(['/settings', fallback.route], {
        queryParams: deniedQueryParams,
      });
    }

    return router.createUrlTree(['/'], { queryParams: deniedQueryParams });
  };
}
